import { baseUrl } from '@/constants/apiConstant';

import { SEOData } from '@/types/seo';

import { capitalizeLocationName } from '../formatItems';

interface SlugInfo {
  type: 'country' | 'state' | 'city';
  city_name?: string;
  city_url?: string;
  state_name?: string;
  state_url?: string;
  country_name?: string;
  country_url?: string;
  categoryFilterValue?: string;
  courseName?: string;
  categoryType?: string;
  courseUrl?: string;
  specializationName?: string;
  specializationUrl?: string;
}

const generateClgListSEOData = (slugInfo: SlugInfo, collegesParam: string, seoData: SEOData): SEOData => {
  const formatCategory = (category?: string) =>
    category?.replace(/-/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase()) ?? '';

  const getLocationDetails = (collegesParam: string): { locationName: string; pageCanonicalUrl: string } => {
    const trimmedParam = collegesParam?.trim();

    const match = trimmedParam.match(/colleges-in-(.+)/);
    const locationName = match ? match[1].replace(/-/g, ' ') : '';
    const pageCanonicalUrl = match ? `colleges-in-${match[1]}` : '';

    return { locationName, pageCanonicalUrl };
  };

  let categoryVal = '';

  // Case 1: If both `specializationName` and `courseName` exist → "B-Tech in Computer Science"
  if (slugInfo?.specializationName && slugInfo?.courseName) {
    categoryVal = `${slugInfo.courseName} in ${slugInfo.specializationName}`;
  }

  // Case 2: If it's a `course` category, use `courseName` OR fallback to `categoryFilterValue`
  else if (slugInfo?.categoryType === 'course') {
    categoryVal = slugInfo.courseName ?? formatCategory(slugInfo?.categoryFilterValue);
  }

  // Case 3: If it's neither of the above, just use `categoryFilterValue`
  else {
    categoryVal = formatCategory(slugInfo?.categoryFilterValue);
  }

  // const { locationName } = getLocationDetails(slugInfo, collegesParam);
  const { locationName } = getLocationDetails(collegesParam);

  const capitalizeLocation = capitalizeLocationName(locationName);
  // if collegesParam includess iit-colleges-in-, nit-colleges-in-, or government-colleges-in- then categoryText must be iit, nit, or government
  const specialMatch = collegesParam.match(/(iit|nit|government)-colleges-in-/i);

  let categoryText;

  if (specialMatch) {
    categoryText =
      specialMatch[1] === 'government'
        ? String(specialMatch[1]).charAt(0).toUpperCase() + String(specialMatch[1]).slice(1) + ' '
        : specialMatch[1].toUpperCase() + ' ';
  } else {
    categoryText = categoryVal ? `${categoryVal} ` : '';
  }

  const baseKeywords = [
    `best ${categoryText}colleges in ${locationName.toLowerCase()}`,
    `top ${categoryText}colleges in ${locationName.toLowerCase()}`,
    `top 10 ${categoryText}colleges in ${locationName.toLowerCase()}`,
    `tier 1 ${categoryText}colleges in ${locationName.toLowerCase()}`,
    `college rankings`,
    `top 50 universities in ${locationName.toLowerCase()}`,
  ].join(', ');

  const page_title = slugInfo?.specializationName
    ? `Top ${categoryText}Colleges in ${capitalizeLocation} 2025`
    : `Top ${categoryText}Colleges in ${capitalizeLocation} 2025: Ranking, Fees, Courses`;

  const page_og_title = seoData?.page_og_title
    ? seoData?.page_og_title
    : `Top ${categoryText}Colleges in ${capitalizeLocation} 2025 | CollegeHai`;

  const page_og_description = seoData?.page_og_description
    ? seoData?.page_og_description
    : `Explore the best ${categoryText}colleges in ${capitalizeLocation}. Find the top rankings, fees, courses, and much more for 2025.`;

  // Twitter Metadata
  const twitter_title = seoData?.twitter_title
    ? seoData?.twitter_title
    : `Top ${categoryText}Colleges in ${capitalizeLocation} 2025 | CollegeHai`;

  const twitter_description = seoData?.twitter_description
    ? seoData?.twitter_description
    : `Find the best colleges in ${capitalizeLocation} for ${categoryText} in 2025. Explore rankings, fees, courses, and more at CollegeHai.`;

  const twitter_image = seoData?.twitter_image ? seoData?.twitter_image : `${baseUrl}/images/logo.png`;
  const twitter_image_alt = seoData?.twitter_image_alt ? seoData?.twitter_image_alt : 'CollegeHai Blog Thumbnail';

  return {
    page_title: seoData?.page_title ?? `${page_title}`,
    page_title_content: page_title,
    page_description: seoData?.page_description
      ? seoData?.page_description
      : `Discover the best ${categoryText}colleges in ${capitalizeLocation} with our detailed college rankings. Explore top 10 ${categoryText}colleges in ${capitalizeLocation}, tier 1 ${categoryText}colleges, and top 50 universities.`,
    // page_content_description: `Discover the **Best ${categoryText}Colleges in ${capitalizeLocation}**, including **tier 1 ${categoryText} in ${capitalizeLocation}** with exceptional rankings. Explore the **Top 10 ${categoryText}Colleges in ${capitalizeLocation}** and **Top 10 Universities in ${capitalizeLocation}** known for academic excellence with **best placement ${categoryText}colleges in ${capitalizeLocation}** to shape your future with confidence.`,
    page_canonical_url: `${baseUrl}/${collegesParam}`,
    keywords: seoData?.keywords ?? baseKeywords,
    page_og_title,
    page_og_description,
    page_og_url: `${baseUrl}/${collegesParam}`,
    twitter_title,
    twitter_description,
    twitter_image,
    twitter_image_alt,
  };
};

export default generateClgListSEOData;
